import { useEffect, useState } from 'react'
import { render } from 'react-dom'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { errorHandler } from 'components/ErrorHandler'

import { ConfirmationModal } from 'featureSliced/features/ConfirmationModal'
import { DataTrans } from 'featureSliced/features/Datatrans/DataTrans'

import { ITableCloudItem } from 'featureSliced/shared/store/reducers/QRMenuTableReducer/types'
import { IBucketProduct } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { qrMenuPOST } from 'featureSliced/shared/api/qrMenu/POST'

import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useOpenModal } from 'featureSliced/shared/hooks/useOpenModal'

import { getDiscountedPrice } from 'featureSliced/shared/utils/getDiscountedPrice'
import { getParsedMenuItems } from 'featureSliced/shared/utils/getParsedMenuItems'
import { loadAnimation } from 'featureSliced/shared/utils/loadAnimation'
import { AnimationData } from 'featureSliced/shared/utils/animations'
import { getNavLink } from 'featureSliced/shared/utils/getNavLink'
import { openLink } from 'featureSliced/shared/utils/openLink'
import {
  getPrecision,
  roundValueTo,
} from 'featureSliced/shared/utils/roundValueTo'
import { qrMenuGET } from '../api/qrMenu/GET'
import { useAdyen } from './useAdyen/useAdyen'
import { AdyenPaymentInitType } from '../api/qrMenu/types'
import { ReceiptModal } from 'components/ReceiptModal'
import { QRMBucketSlice } from '../store/reducers/QRMenuBucketReducer'
import { useMyDispatch } from './useMyDispatch'
import { getProductNote } from '../utils/getProductNote'

export const usePayment = () => {
  // Hooks
  const startAdyen = useAdyen()
  const t = useTranslations()
  const dispatch = useMyDispatch()
  const navigate = useNavigate()
  const openModal = useOpenModal()
  const [searchParams] = useSearchParams()
  const table = searchParams.get('table')
  const Currency = useMySelector((state) => state.app.companyData.Currency)

  // Store
  const clearBucket = QRMBucketSlice.actions.clearBucket
  const appTheme = useMySelector((state) => state.app.interfaceStyles.appTheme)
  const paymentVariant = useMySelector((state) => state.app.menu.paymentVariant)
  const adyenShopId = useMySelector(
    (state) => state.app.paymentConfig?.hsPay?.shopId
  )
  const paymentProvider = useMySelector(
    (state) => state.app.paymentConfig?.paymentProviderType
  )

  // State
  const [datatransPaymentId, setDatatransPaymentId] = useState<string | null>(
    null
  )

  // useEffect
  useEffect(() => {
    if (datatransPaymentId) {
      render(
        <DataTrans
          key={datatransPaymentId}
          transactionId={datatransPaymentId}
        />,
        document.getElementById('datatransContainer')
      )
    }
  }, [datatransPaymentId])

  const openRetryConfirmation = async (
    productsToPay: ITableCloudItem[] | IBucketProduct[],
    totalNoTips = 0,
    tipsPercentage = 0,
    useSavedCard = false,
    useCustomerCard = false,
    updateActiveCard = false,
    index = 0
  ) => {
    let failedOrderJSON: AnimationData | null = null
    try {
      failedOrderJSON = await loadAnimation(`${appTheme}/callWaiter.json`)
    } catch {
      failedOrderJSON = await loadAnimation('light/failedOrder.json')
    }

    openModal({
      id: `CONF_MODAL_${index}`,
      title: '',
      components: [
        ({ closeThisModal }) => (
          <ConfirmationModal
            onConfirm={() => {
              startQRMAfterMealPayment(
                productsToPay,
                totalNoTips,
                tipsPercentage,
                useSavedCard,
                useCustomerCard,
                updateActiveCard,
                index + 1
              )
              closeThisModal()
            }}
            onCancel={() => {
              closeThisModal()
              navigate(getNavLink(searchParams, ''))
            }}
            okText={t('buttons.tryAgain')}
            animationJSON={failedOrderJSON}
            text={t('errors.createOrderFail')}
          />
        ),
      ],
    })
  }

  const startQRMAfterMealPayment = async (
    productsToPay: ITableCloudItem[] | IBucketProduct[],
    totalNoTips = 0,
    tipsPercentage = 0,
    useSavedCard = false,
    useCustomerCard = false,
    updateActiveCard = false,
    index = 0
  ) => {
    try {
      ///
      const makePaymentRequest = async (
        shopId?: string,
        hsPaymentData?: any
      ) => {
        return await qrMenuPOST.paymentRequest(
          paymentVariant === 1,
          tipsPercentage,
          table,
          useSavedCard,
          useCustomerCard,
          updateActiveCard,
          productsToPay.length
            ? productsToPay?.map((el) => {
                return {
                  ...el,
                  discountP: el.discountP ?? 0,
                  discountType: el.discountP ? 50 : 0,
                  // total: (el.price ?? 0) * (el.count ?? 1),
                  total: roundValueTo(
                    getDiscountedPrice(
                      (el.price ?? 0) * (el.count ?? 1),
                      el.discountP
                    ),
                    getPrecision(Currency)
                  ),
                  freeText: getProductNote(el.productId, el.categoryId),
                  menuItems: getParsedMenuItems(el.menuItems),
                }
              })
            : undefined,
          shopId,
          hsPaymentData
        )
      }

      if (paymentProvider === 3 && adyenShopId?.length && !useCustomerCard) {
        // ADYEN
        startAdyen(
          // adyenPaymentMethods,
          Math.round((totalNoTips + tipsPercentage) * 100),
          // TODO: add type if possible.
          async (hsPaymentData: any) => {
            const result = await makePaymentRequest(adyenShopId, hsPaymentData)

            // TODO: isnt dry. is also used in Refill
            const hasIpr =
              result !== null &&
              typeof result === 'object' &&
              'InitPaymentResult' in result

            const ipr = hasIpr
              ? (result as AdyenPaymentInitType).InitPaymentResult
              : undefined

            return {
              paymentKey: ipr?.paymentKey,
              resultCode:
                ipr?.resultCode || ipr?.paymentKey ? 'Authorised' : 'Error',
              action: ipr?.action,
            }
          },
          (transactionId: string | null) =>
            openModal({
              id: 'RECEIPT_MODAL',
              shouldHideCross: true,
              components: [
                ({ closeThisModal }) => (
                  <ReceiptModal
                    paymentKey={transactionId}
                    onCloseModal={() => {
                      closeThisModal()
                      dispatch(clearBucket())
                      window.localStorage.removeItem('products_notes')
                    }}
                  />
                ),
              ],
            })
        )
        navigate('/')
      } else {
        // DataTrans or SaferPay or Adyen (HS-Pay)
        const res = await makePaymentRequest()

        if (res?.PaymentProvider === 1 && res?.TransactionId) {
          if (res?.PaymentType === 'default') {
            setDatatransPaymentId(res?.TransactionId)
          }
          if (res?.PaymentType === 'customerCard') {
            // loyalty card
            // customer card case
            navigate(`/?datatransTrxId=${res?.TransactionId}`)
          }
          if (res?.PaymentType === 'savedCard') {
            // saved card case
            navigate(`/?datatransTrxId=${res?.TransactionId}`)
          }
        }

        if (res?.PaymentProvider === 2) {
          if (res?.RedirectUrl) {
            // default and savedCard must be redirected
            openLink(res?.RedirectUrl)
          } else if (res?.PaymentType === 'customerCard') {
            // loyalty card
            navigate(`/?orderGuid=${res?.Token}`)
            // navigate(`/?datatransTrxId=${res?.Token}`)
          }
        }

        if (res?.PaymentProvider === 3 && res?.InitPaymentResult?.paymentKey) {
          if (res?.PaymentType === 'customerCard') {
            // loyalty card case
            navigate(`/?datatransTrxId=${res?.InitPaymentResult?.paymentKey}`)
          }
          if (res?.PaymentType === 'savedCard') {
            //   // saved card case
            //   navigate(`/?datatransTrxId=${res?.TransactionId}`)
          }
          // if (res?.PaymentType === 'default') {
          //   NOTE: not needed. default case is being handled within useAdyen hook
          // }
        }

        return res
      }
    } catch (e) {
      errorHandler(e)
      openRetryConfirmation(
        productsToPay,
        totalNoTips,
        tipsPercentage,
        useSavedCard,
        useCustomerCard,
        updateActiveCard,
        index
      )
    }
  }

  return {
    startQRMAfterMealPayment,
  }
}
